/* Remove the outer white background on product images.
   Flood-fills near-white pixels starting from the four edges; any white
   that's surrounded by non-white (e.g. label text on a bottle) is left
   alone because the flood fill never reaches it. Result replaces the
   original src via a blob URL — runs once per <img>, lazy-tolerant. */

const SELECTOR = [
  '.product__media img',
  '.product__media-item img',
  '.card__media img',
  '.card-wrapper img',
  '.product-card-wrapper img',
  '.grid__item .card img',
  'product-media img',
  '.media img'
].join(', ');

const TOLERANCE = 24;          /* how close to pure white still counts as white */
const FEATHER_HIGH = 230;      /* edge pixel: very light → strong fade */
const FEATHER_LOW = 195;       /* edge pixel: lightish → partial fade */
const FEATHER_RADIUS = 4;      /* depth in px over which the fade ramps inward from the boundary */

const seen = new WeakSet();

function isWhiteAt(data, i) {
  return data[i + 3] > 0
    && data[i] >= 255 - TOLERANCE
    && data[i + 1] >= 255 - TOLERANCE
    && data[i + 2] >= 255 - TOLERANCE;
}

/* Cheap pre-check before the full pass. The flood fill below seeds ONLY from
   near-white pixels on the four 1-px edges, and the feather pass only grows
   from flooded pixels, so an image with no such edge pixel (every bottle
   asset, the transparent product PNGs) comes out pixel-identical: its blob
   would only re-encode the input as a PNG, drop srcset and register a late
   LCP entry. Each edge is drawn with the same drawImage(source, x, y) call as
   the full pass, offset by whole pixels into a w x 1 / 1 x h canvas, so no
   full-size buffer is allocated. PRECHECK_MIN is deliberately a few levels
   looser than isWhiteAt: any doubt falls through to the unchanged full pass. */
const PRECHECK_MIN = 255 - TOLERANCE - 8;

function edgeMayHaveWhite(source, w, h) {
  const edges = [
    [w, 1, 0, 0],          /* top row */
    [w, 1, 0, 1 - h],      /* bottom row */
    [1, h, 0, 0],          /* left column */
    [1, h, 1 - w, 0]       /* right column */
  ];
  for (let e = 0; e < edges.length; e += 1) {
    const cw = edges[e][0];
    const ch = edges[e][1];
    const strip = document.createElement('canvas');
    strip.width = cw;
    strip.height = ch;
    const sctx = strip.getContext('2d', { willReadFrequently: true });
    sctx.drawImage(source, edges[e][2], edges[e][3]);
    const px = sctx.getImageData(0, 0, cw, ch).data;
    for (let i = 0; i < px.length; i += 4) {
      if (px[i + 3] > 0
        && px[i] >= PRECHECK_MIN
        && px[i + 1] >= PRECHECK_MIN
        && px[i + 2] >= PRECHECK_MIN) return true;
    }
  }
  return false;
}

function processImage(srcImg) {
  const url = srcImg.currentSrc || srcImg.src;
  if (!url || url.startsWith('data:') || url.startsWith('blob:')) return;

  /* Reload through a fresh Image with CORS so getImageData() doesn't
     trip the canvas-tainted security check. Shopify's CDN sends
     access-control-allow-origin: *, so anonymous CORS succeeds. */
  const cors = new Image();
  cors.crossOrigin = 'anonymous';
  cors.decoding = 'async';

  const run = () => {
    const w = cors.naturalWidth;
    const h = cors.naturalHeight;
    if (!w || !h) return;

    try {
      if (!edgeMayHaveWhite(cors, w, h)) return; /* nothing to trim: keep src/srcset/sizes */
    } catch (e) {
      /* pre-check failed: fall through to the full pass, as before */
    }

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(cors, 0, 0);

    let img;
    try {
      img = ctx.getImageData(0, 0, w, h);
    } catch (e) {
      return; /* CORS blocked despite our request — give up silently */
    }

    const data = img.data;
    const visited = new Uint8Array(w * h);
    const stack = [];

    /* Seed every edge pixel that's already white. */
    const seed = (idx) => {
      if (visited[idx]) return;
      if (!isWhiteAt(data, idx * 4)) return;
      visited[idx] = 1;
      stack.push(idx);
    };
    for (let x = 0; x < w; x += 1) {
      seed(x);
      seed((h - 1) * w + x);
    }
    for (let y = 0; y < h; y += 1) {
      seed(y * w);
      seed(y * w + (w - 1));
    }

    /* Iterative flood fill (4-connectivity). Marks every reachable
       near-white pixel transparent. */
    while (stack.length > 0) {
      const idx = stack.pop();
      data[idx * 4 + 3] = 0;
      const x = idx % w;
      const y = (idx - x) / w;

      if (x > 0) {
        const n = idx - 1;
        if (!visited[n] && isWhiteAt(data, n * 4)) { visited[n] = 1; stack.push(n); }
      }
      if (x < w - 1) {
        const n = idx + 1;
        if (!visited[n] && isWhiteAt(data, n * 4)) { visited[n] = 1; stack.push(n); }
      }
      if (y > 0) {
        const n = idx - w;
        if (!visited[n] && isWhiteAt(data, n * 4)) { visited[n] = 1; stack.push(n); }
      }
      if (y < h - 1) {
        const n = idx + w;
        if (!visited[n] && isWhiteAt(data, n * 4)) { visited[n] = 1; stack.push(n); }
      }
    }

    /* Feather: ramp alpha inward over FEATHER_RADIUS pixels so the boundary
       blends instead of snapping. Distance is computed by a BFS that only
       walks through light-ish pixels, so dark content (text, logos) acts as
       a barrier and stays crisp. */
    const distance = new Uint8Array(w * h);
    const queue = [];

    /* Seed: light pixels touching a transparent neighbour start at distance 1. */
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        const idx = y * w + x;
        if (visited[idx]) continue;
        const i = idx * 4;
        const lightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
        if (lightness < FEATHER_LOW) continue;
        const left = x > 0 && visited[idx - 1];
        const right = x < w - 1 && visited[idx + 1];
        const up = y > 0 && visited[idx - w];
        const down = y < h - 1 && visited[idx + w];
        if (!(left || right || up || down)) continue;
        distance[idx] = 1;
        queue.push(idx);
      }
    }

    /* Expand the distance field through other light pixels until FEATHER_RADIUS. */
    let head = 0;
    while (head < queue.length) {
      const idx = queue[head];
      head += 1;
      const d = distance[idx];
      if (d >= FEATHER_RADIUS) continue;
      const x = idx % w;
      const y = (idx - x) / w;
      const nd = d + 1;
      const step = (n) => {
        if (visited[n] || distance[n] !== 0) return;
        const ni = n * 4;
        const nl = (data[ni] + data[ni + 1] + data[ni + 2]) / 3;
        if (nl < FEATHER_LOW) return;
        distance[n] = nd;
        queue.push(n);
      };
      if (x > 0) step(idx - 1);
      if (x < w - 1) step(idx + 1);
      if (y > 0) step(idx - w);
      if (y < h - 1) step(idx + w);
    }

    /* Apply the fade: strongest at d=1, easing out to ~zero by d=FEATHER_RADIUS. */
    for (let idx = 0; idx < w * h; idx += 1) {
      const d = distance[idx];
      if (d === 0) continue;
      const i = idx * 4;
      const lightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
      const t = 1 - (d - 1) / FEATHER_RADIUS;
      const ease = t * t * (3 - 2 * t);
      const baseReduce = lightness >= FEATHER_HIGH ? 220 : 130;
      data[i + 3] = Math.max(0, data[i + 3] - Math.round(baseReduce * ease));
    }

    ctx.putImageData(img, 0, 0);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const blobUrl = URL.createObjectURL(blob);
      /* Drop srcset/sizes — otherwise the browser keeps loading the
         original Shopify CDN file and overrides our blob. */
      if (srcImg.hasAttribute('srcset')) srcImg.removeAttribute('srcset');
      if (srcImg.hasAttribute('sizes')) srcImg.removeAttribute('sizes');
      const parent = srcImg.parentElement;
      if (parent && parent.tagName === 'PICTURE') {
        parent.querySelectorAll('source').forEach((s) => s.removeAttribute('srcset'));
      }
      srcImg.src = blobUrl;
    }, 'image/png');
  };

  /* Decode first (off the main thread where supported) so drawImage() does
     not decode the full-size file synchronously. If decode() is missing or
     rejects, carry on exactly as before. */
  cors.onload = () => {
    if (typeof cors.decode === 'function') {
      cors.decode().then(run, run);
    } else {
      run();
    }
  };

  cors.onerror = () => { /* keep the original image */ };
  cors.src = url;
}

/* Complete images far outside the viewport at scan time wait for an idle
   slot (at most ~1 s) so they don't compete with the first paint. Images
   on or near the screen, and images still loading (processed on their own
   'load' event, as before; lazy ones only load near the viewport), are
   never delayed, so a white-bordered photo can't flip after it scrolls in. */
const whenIdle = 'requestIdleCallback' in window
  ? (fn) => window.requestIdleCallback(fn, { timeout: 1000 })
  : (fn) => window.setTimeout(fn, 200);

function nearViewport(img) {
  const r = img.getBoundingClientRect();
  const vw = window.innerWidth || document.documentElement.clientWidth;
  const vh = window.innerHeight || document.documentElement.clientHeight;
  return r.bottom >= -vh && r.top <= 2 * vh && r.right >= -vw && r.left <= 2 * vw;
}

function scan(root = document) {
  const imgs = root.querySelectorAll(SELECTOR);
  imgs.forEach((img) => {
    /* Generated card assets (bottle-*.webp) have fully transparent borders:
       nothing to trim, so skip the CORS reload and the canvas work. */
    if (img.hasAttribute('data-azan-no-trim')) return;
    if (seen.has(img)) return;
    seen.add(img);

    if (img.complete && img.naturalWidth > 0) {
      if (nearViewport(img)) {
        processImage(img);
      } else {
        whenIdle(() => processImage(img));
      }
    } else {
      img.addEventListener('load', () => processImage(img), { once: true });
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scan());
} else {
  scan();
}

/* Watch for new product images injected by AJAX (quick-view, filters,
   infinite scroll, Theme Editor section reloads). */
if ('MutationObserver' in window) {
  const mo = new MutationObserver((records) => {
    for (let i = 0; i < records.length; i += 1) {
      const rec = records[i];
      for (let j = 0; j < rec.addedNodes.length; j += 1) {
        const node = rec.addedNodes[j];
        if (node.nodeType !== 1) continue;
        scan(node);
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
}

document.addEventListener('shopify:section:load', (event) => scan(event.target));
